import React, { useEffect, useState } from 'react';
import {
  Table,
  InputNumber,
  Button,
  Card,
  notification,
  Form,
  Popconfirm,
  Space,
  Typography,
  Row,
  Col,
} from 'antd';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import FindByKeySalesTargetRankingViewModel from '../viewmodels/sales-target-ranking.viewmodel';
import { ConfigSystemHttpRepository } from '../adapters/repositories';
import { UpdateSalesTargetSettingUsecase } from '@/domains/config-system/usecases/update-sales-target-setting.usecase';
import { RankingConfigurationPayload } from '@/domains/config-system/ports/payloads';
import { SalesTargetRankingValidation } from '../validations/sales-target-ranking.validation';
import ConfigHistory from '../components/config-history';

const { Title, Text } = Typography;

interface RankConfig {
  rank: string;
  min: number;
  max: number | null;
  id?: string;
}

interface FormValues {
  ranks: RankConfig[];
}

const SalesRankConfigScreen: React.FC = () => {
  const {
    data: apiData,
    actionFetchData,
    loading,
    history,
  } = FindByKeySalesTargetRankingViewModel();
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      ranks: [],
    },
    resolver: yupResolver(SalesTargetRankingValidation.createValidationSchema()),
  });

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'ranks',
  });

  useEffect(() => {
    if (apiData && Array.isArray(apiData) && apiData.length > 0) {
      const formattedData = apiData.map((item) => ({
        rank: item.rank,
        min: item.values?.[0] || 0,
        max: item.values?.[1] === undefined ? null : item.values[1],
        id: item.id,
      }));

      setValue('ranks', formattedData);
    }
  }, [apiData, setValue]);

  useEffect(() => {
    clearErrors();
    if (formErrors) {
      setFormErrors(null);
    }
  }, [watch('ranks')]);

  const formValues = watch('ranks');

  const columns = [
    {
      title: 'Sales Target Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: '20%',
      render: (text: string) => (
        <div className='font-medium text-center'>
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: (
        <div>
          <div>Min KPI (mil)</div>
          <div className='text-gray-400 text-xs'>[Range: ≥ value]</div>
        </div>
      ),
      dataIndex: 'min',
      key: 'min',
      width: '30%',
      render: (text: string, record: any, index: number) => (
        <Form.Item
          validateStatus={errors.ranks?.[index]?.min ? 'error' : ''}
          help={errors.ranks?.[index]?.min?.message}
          className='mb-0'
        >
          <InputNumber
            min={0}
            value={formValues[index]?.min}
            onChange={(val) => {
              update(index, { ...formValues[index], min: val || 0 });
            }}
            style={{ width: '100%' }}
            size='large'
            className='w-full'
            placeholder='≥ 0'
          />
        </Form.Item>
      ),
    },
    {
      title: (
        <div>
          <div>Max KPI (mil)</div>
          <div className='text-gray-400 text-xs'>[Range: &lt; value]</div>
        </div>
      ),
      dataIndex: 'max',
      key: 'max',
      width: '30%',
      render: (text: string, record: any, index: number) => (
        <Form.Item
          validateStatus={errors.ranks?.[index]?.max ? 'error' : ''}
          help={errors.ranks?.[index]?.max?.message}
          className='mb-0'
        >
          <InputNumber
            min={0}
            value={formValues[index]?.max ?? undefined}
            placeholder='∞ (No limit)'
            onChange={(val) => {
              update(index, { ...formValues[index], max: val });
            }}
            style={{ width: '100%' }}
            size='large'
            className='w-full'
          />
        </Form.Item>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      align: 'center',
      render: (text: string, record: any, index: number) => (
        <Popconfirm
          title='Remove this rank?'
          description='Are you sure you want to remove this rank?'
          onConfirm={() => handleRemoveRank(index)}
          okText='Yes'
          cancelText='No'
          placement='left'
        >
          <Button
            danger
            type='text'
            icon={<DeleteOutlined />}
            disabled={formValues.length <= 1}
            className='hover:bg-red-50'
          />
        </Popconfirm>
      ),
    },
  ];

  const handleCreateRank = (): void => {
    if (formValues.length === 0) {
      append({ rank: 'A', min: 0, max: 0, id: undefined });
      return;
    }

    const lastRank = formValues[formValues.length - 1];
    const nextRankLetter = String.fromCharCode(lastRank.rank.charCodeAt(0) + 1);

    append({ rank: nextRankLetter, min: 0, max: 0, id: undefined });
  };

  const handleRemoveRank = (index: number): void => {
    if (formValues.length <= 1) {
      return;
    }
    remove(index);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      setIsSaving(true);
      setFormErrors(null);

      const apiFormatData: RankingConfigurationPayload[] = data.ranks.map((item) => ({
        id: item?.id,
        rank: item.rank,
        values: item.max ? [item.min, item.max] : [item.min],
      }));

      const configSystemRepository = new ConfigSystemHttpRepository();
      const updateSalesTargetSettingUsecase = new UpdateSalesTargetSettingUsecase(
        configSystemRepository,
      );

      await updateSalesTargetSettingUsecase.run(apiFormatData);

      notification.success({
        message: 'Configuration saved successfully',
        placement: 'topRight',
      });

      actionFetchData();
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Failed to save configuration',
        placement: 'topRight',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleError = (errors: any) => {
    console.error(errors);
    if (errors.ranks) {
      if (typeof errors.ranks.message === 'string') {
        setFormErrors(errors.ranks.message);
      } else {
        const firstError = errors.ranks.find((item: any) => item && Object.keys(item).length > 0);
        if (firstError) {
          const errorKey = Object.keys(firstError)[0];
          setFormErrors(firstError[errorKey].message);
        }
      }
    }
  };

  return (
    <div className='p-6 max-w-7xl mx-auto'>
      <Row gutter={24}>
        <Col span={16}>
          <Card
            title={
              <Title level={3} className='!mb-0'>
                Sales Target Rank Configuration
              </Title>
            }
            className='shadow-lg'
            extra={
              <Text type='secondary'>Configure the KPI ranges for each sales target rank</Text>
            }
          >
            <form onSubmit={handleSubmit(onSubmit, handleError)}>
              {formErrors && (
                <div className='mb-4 p-4 bg-red-50 border border-red-100 rounded-lg'>
                  <Text type='danger'>{formErrors}</Text>
                </div>
              )}

              <Table
                dataSource={fields.map((field, idx) => ({ ...field, key: idx }))}
                columns={columns as any}
                pagination={false}
                bordered
                loading={loading}
                className='mb-6'
                rowClassName='hover:bg-gray-50'
              />

              <Space className='w-full justify-between'>
                <Button
                  type='dashed'
                  onClick={handleCreateRank}
                  icon={<PlusOutlined />}
                  size='large'
                >
                  Add New Rank
                </Button>

                <Button
                  type='primary'
                  htmlType='submit'
                  loading={isSaving}
                  icon={<SaveOutlined />}
                  size='large'
                  className='bg-blue-600 hover:bg-blue-700'
                >
                  Save Configuration
                </Button>
              </Space>
            </form>
          </Card>
        </Col>
        <Col span={8}>
          <ConfigHistory history={history} />
        </Col>
      </Row>
    </div>
  );
};

export default SalesRankConfigScreen;
